import React, {
	ReactNode,
	useEffect,
	useMemo,
	useRef,
	useState,
	useTransition,
} from 'react';

import { TourStepProps } from 'antd';
import cx from 'classnames';
import { useLocation } from 'react-router-dom';
import { isEmpty } from 'lodash';

import themeConfig from '@/theme/themeConfig';
import { Button, ConfigProvider, Tour, Typography } from '@/widgets';
import {
	COACHMARK_VIEWED,
	COACH_MARK_ATTRIBUTE,
	CoachMarkGroupId,
	ICoachMarkStep,
	TEST_IDS,
	TOUR_CLOSED,
	TOUR_SKIPPED,
} from '@/constants';
import { sendClickEvent } from '@/utils/AnalyticsUtils';

import styles from './CoachMark.module.scss';
import ProgressIndicator from './ProgressIndicator';

interface ICoachMarkProps<T extends Readonly<ICoachMarkStep[]>> {
	isOpen: boolean;
	steps: T;
	/**
	 *  1-based indexing
	 */
	startStep?: number;
	onClose?: (groupId: CoachMarkGroupId, isSkipped: boolean) => void;
	onChange?: (groupId: CoachMarkGroupId, coachMarkId: string) => void;
	onCleanup: () => void;
}

/**
 *
 * @description UI component for PFB Coachmark
 *
 * @author  Rohit Niraj Singh <@rohit.singh3>
 *
 * @returns {React.ReactElement}
 */

export default function CoachMark<T extends Readonly<ICoachMarkStep[]>>({
	steps,
	isOpen,
	startStep = 1,
	onClose,
	onChange,
	onCleanup,
}: ICoachMarkProps<T>) {
	const [currentStep, setCurrentStep] = useState(startStep);
	const [isReady, setIsReady] = useState(false);
	const [, startTransition] = useTransition();
	const currentGroupId = useRef(steps[currentStep - 1]?.groupId);
	const delayTimeoutId = useRef<NodeJS.Timeout>();
	const location = useLocation();

	const closeCoachmark = () => {
		sendClickEvent({ name: TOUR_CLOSED });
		onClose?.(currentGroupId.current, false);
	};

	const skipCoachmark = () => {
		sendClickEvent({ name: TOUR_SKIPPED });
		onClose?.(currentGroupId.current, true);
	};

	useEffect(() => {
		startTransition(() => {
			setIsReady(true);
		});
	}, []);

	useEffect(() => {
		return () => {
			clearTimeout(delayTimeoutId.current);
			onCleanup?.();
		};
	}, [location]);

	useEffect(() => {
		setIsReady(false);
		if (steps.length > 0) {
			delayTimeoutId.current = setTimeout(() => {
				setIsReady(true);
				setCurrentStep(startStep);
				currentGroupId.current = steps[startStep - 1]?.groupId;
				onChange?.(currentGroupId.current, steps[startStep - 1]?.id);
				clearTimeout(delayTimeoutId.current);
			}, 500);
		}
	}, [steps]);

	const updateStep = (current: number) => {
		setCurrentStep(current + 1);
		currentGroupId.current = steps[current].groupId;
		onChange?.(currentGroupId.current, steps[current]?.id);
		sendClickEvent({ name: COACHMARK_VIEWED, value: `${steps[current].id}` });
	};

	const renderDescriptionWithProgressBar = (description: ReactNode) => {
		return (
			<div className={styles.descriptionWrapper}>
				{description ? (
					<Typography className={styles.description} as="body" weight="regular">
						{description}
					</Typography>
				) : null}
				<ProgressIndicator
					currentStep={currentStep}
					stepsCount={steps.length}
				/>
			</div>
		);
	};

	const renderFooterIndicators = (current: number, total: number) => {
		const step = current + 1;

		return (
			<div
				className={cx(
					styles.footerIndicators,
					step === total && styles.lastStep,
				)}
			>
				<Button
					size="small"
					type="link"
					className={styles.skipTour}
					onClick={skipCoachmark}
					data-id={TEST_IDS.COACH_MARK.SKIP_TOUR}
				>
					Skip tour
				</Button>
				<Typography
					as="footnote"
					weight="regular"
					className={styles.stepIndicator}
				>
					{step}/{total}
				</Typography>
			</div>
		);
	};

	const renderTitle = (title: ReactNode) => {
		return (
			<Typography as="h2" weight="bold">
				{title}
			</Typography>
		);
	};

	const tourSteps = useMemo(() => {
		return steps.map(({ id, title, description, ...stepsConfig }) => {
			return {
				...stepsConfig,
				title: renderTitle(title),
				description: renderDescriptionWithProgressBar(description),
				target: () =>
					document.querySelector(`[${COACH_MARK_ATTRIBUTE}="${id}"]`),
				onClose: () => onClose?.(currentGroupId.current, true),
				nextButtonProps: {
					children: (
						<Typography
							data-id={TEST_IDS.COACH_MARK.NEXT_BTN}
							className={cx(styles.navigationBtn)}
							as="body"
							weight="semibold"
						>
							{currentStep === steps.length ? 'Finish' : 'Next'}
						</Typography>
					),
				},
				prevButtonProps: {
					children: (
						<Typography
							data-id={TEST_IDS.COACH_MARK.PREV_BTN}
							className={cx(styles.navigationBtn, styles.previousBtn)}
							as="body"
							weight="semibold"
						>
							Previous
						</Typography>
					),
				},
			};
		}) as TourStepProps[];
	}, [steps, currentStep]);

	return isReady && isOpen ? (
		<ConfigProvider
			theme={{
				token: {
					colorPrimary: themeConfig.primary,
				},
			}}
		>
			{isOpen && !isEmpty(tourSteps) && (
				<div className={styles.coachmarkOverlay}></div>
			)}

			<Tour
				arrow={{
					pointAtCenter: false,
				}}
				current={currentStep - 1}
				onChange={updateStep}
				rootClassName={styles.coachMark}
				indicatorsRender={renderFooterIndicators}
				steps={tourSteps}
				open={isOpen}
				onClose={closeCoachmark}
			/>
		</ConfigProvider>
	) : null;
}
